import React, { useCallback, useEffect, useMemo } from "react";

import {
	faBolt,
	faChevronLeft,
	faChevronRight,
	faCog,
	faEllipsisV,
	faRedo,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faBolt as faBoltS } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import cx from "classnames";
import { RelayObservable } from "relay-runtime/lib/network/RelayObservable";

import AppContext from "~/common/AppContext";
import { CRM_USER_SETTINGS_UPDATED_EVENT } from "~/settings/Integrations/utils";
import type { HubspotPanelNode } from "~/stream/SideBar/CRMPanel/Hubspot";
import { SideBarItem } from "~/stream/SideBar/SideBarItem";
import { LoadingSpinner, Tooltip } from "~/ui";
import { ButtonLink, ContactSupportLink, IconButton } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";
import toast from "~/ui/toast";

import type { HubspotQuery$data } from "~/__generated__/HubspotQuery.graphql";
import type { HubspotSendNotesAutomationStatusQuery$data } from "~/__generated__/HubspotSendNotesAutomationStatusQuery.graphql";

import styles from "./css/HubspotPanel.module.css";

export interface ModalProviderProps<CRMPanelNode> {
	noteId: string;
	onClose: () => void;
	options: readonly CRMPanelNode[];
	defaultOption?: CRMPanelNode;
	modalMode: boolean;
	sendNotesAutomationIsActive?: boolean;
	shouldShowAutomationInfoBox?: boolean;
}

export interface HubspotInnerPaneProps<CRMPanelNode> {
	activeNode: CRMPanelNode;
}

export function CRMItem({
	logo,
	url,
	title,
	primary,
	secondary,
}: {
	logo: React.ReactNode;
	url?: string;
	title: string;
	primary: string;
	secondary?: string | null;
}) {
	return (
		<div className={styles.item}>
			<div className={styles.itemIcon}>{logo}</div>
			<div>
				{url ? (
					<a className={styles.link} href={url} target="_blank" rel="noopener noreferrer">
						{title}
					</a>
				) : (
					{ title }
				)}
				<br />
				{primary}
				{secondary ? ` · ${secondary}` : ""}
			</div>
		</div>
	);
}

interface PrivateProps<CRMPanelNode extends HubspotPanelNode> {
	streamId: string;
	noteId: string;
	noneFound: React.ReactNode;
	crmQuery: (streamId: string, noteId: string) => RelayObservable<HubspotQuery$data>;
	sendNotesAutomationQuery?: (noteId: string) => RelayObservable<HubspotSendNotesAutomationStatusQuery$data>;
	InnerPane: React.FunctionComponent<HubspotInnerPaneProps<CRMPanelNode>>;
	showFeature: () => void | undefined;
	Modal: React.FunctionComponent<ModalProviderProps<CRMPanelNode>>;
	SendNotesButtonContent: React.ReactNode;
	hasAccessToIntegration: boolean;
}

const HubspotPanel = <CRMPanelNode extends HubspotPanelNode>({
	streamId,
	noteId,
	showFeature,
	noneFound,
	InnerPane,
	Modal,
	crmQuery,
	sendNotesAutomationQuery,
	SendNotesButtonContent,
	hasAccessToIntegration,
}: PrivateProps<CRMPanelNode>): JSX.Element => {
	const [contacts, setContacts] = React.useState<CRMPanelNode[]>(() => []);
	const [loading, setLoading] = React.useState(true);
	const [page, setPage] = React.useState(0);
	const [error, setError] = React.useState<boolean>(false);
	const [sendNotesOpen, setSendNotesOpen] = React.useState(false);
	const [isOptionsMenuOpen, setIsOptionsMenuOpen] = React.useState(false);
	const [sendNotesAutomationIsActive, setSendNotesAutomationIsActive] = React.useState(false);
	const [sendNotesToCrmAutomationIsValid, setSendNotesToCrmAutomationIsValid] = React.useState(false);

	const { openSettingsModal } = React.useContext(AppContext);
	const openIntegrationSettings = React.useCallback(() => {
		openSettingsModal(["apps-and-integrations", "hubspot"]);
	}, [openSettingsModal]);

	const previous = useCallback(() => {
		if (page > 0) {
			setPage(page - 1);
		}
	}, [page]);

	const next = useCallback(() => {
		if (page < contacts.length - 1) {
			setPage(page + 1);
		}
	}, [page, contacts]);

	const fetchAutomationStatus = useCallback(() => {
		if (!sendNotesAutomationQuery) {
			return;
		}

		sendNotesAutomationQuery(noteId)
			.toPromise()
			.then(data => {
				if (!data) {
					return;
				}
				setSendNotesToCrmAutomationIsValid(data.note?.sendNoteToCrmAutomationIsValid ?? false);
				setSendNotesAutomationIsActive(data.note?.sendNoteAutomationIsActive ?? false);
			});
	}, [sendNotesAutomationQuery, noteId]);

	const fetchContent = useCallback(
		(initial = true) => {
			const error = () => {
				setError(true);
				setContacts([]);
				if (!initial) {
					toast.error("Unable to fetch, please try again later");
				}
			};

			setLoading(true);
			crmQuery(streamId, noteId)
				.toPromise()
				.then(data => {
					setLoading(false);
					if (!data) {
						error();
						return;
					}
					setError(false);
					if (!initial) {
						toast.success(`HubSpot content updated`);
					}
					setContacts(data.hubspotContactsForStream as CRMPanelNode[]);
				})
				.catch(() => {
					setLoading(false);
					error();
					return;
				});

			fetchAutomationStatus();
		},
		[crmQuery, streamId, noteId, fetchAutomationStatus],
	);

	const refetchContent = useMemo(() => fetchContent.bind(null, false), [fetchContent]);

	useEffect(() => {
		fetchContent(true);
	}, [fetchContent]);

	useEffect(() => {
		window.addEventListener(CRM_USER_SETTINGS_UPDATED_EVENT, fetchAutomationStatus);
		return () => window.removeEventListener(CRM_USER_SETTINGS_UPDATED_EVENT, fetchAutomationStatus);
	}, [fetchAutomationStatus]);

	const openSendNotes = useCallback(() => {
		if (!hasAccessToIntegration) {
			showFeature();
			return;
		}

		setSendNotesOpen(true);
	}, [hasAccessToIntegration, showFeature]);

	const closeSendNotes = useCallback(() => {
		setSendNotesOpen(false);
	}, []);

	const toggleOptionsOpen = useCallback(() => setIsOptionsMenuOpen(open => !open), []);

	const activeContact = contacts[page];

	return (
		<SideBarItem>
			<SideBarItem.Header>
				<SideBarItem.Title>Contacts</SideBarItem.Title>

				{contacts.length > 1 && (
					<div className={styles.contactSelector}>
						<SideBarItem.HeaderIconButton
							title="Previous"
							icon={faChevronLeft}
							className={styles.arrowBtn}
							onClick={previous}
						/>
						<div className={styles.stepCount}>
							{page + 1}/{contacts.length}
						</div>
						<SideBarItem.HeaderIconButton
							title="Next"
							icon={faChevronRight}
							className={styles.arrowBtn}
							onClick={next}
						/>
					</div>
				)}

				<SideBarItem.HeaderButton onClick={openSendNotes}>{SendNotesButtonContent}</SideBarItem.HeaderButton>

				{sendNotesToCrmAutomationIsValid && (
					<Tooltip
						placement="top"
						content={
							sendNotesAutomationIsActive ? (
								<span>Notes will automatically be sent after the meeting</span>
							) : (
								"Configure Automation"
							)
						}
					>
						<IconButton
							className={styles.boltIcon}
							icon={sendNotesAutomationIsActive ? faBoltS : faBolt}
							onClick={openIntegrationSettings}
						/>
					</Tooltip>
				)}

				<Menu open={isOptionsMenuOpen} onOpenChange={toggleOptionsOpen}>
					<Menu.Trigger>
						<IconButton icon={faEllipsisV} className={styles.ellipsis} />
					</Menu.Trigger>
					<Menu.Content align="end">
						<Menu.Item onSelect={refetchContent} icon={faRedo}>
							Refresh
						</Menu.Item>
						<Menu.Item onSelect={openIntegrationSettings} icon={faCog}>
							Integration settings
						</Menu.Item>
					</Menu.Content>
				</Menu>
			</SideBarItem.Header>
			<SideBarItem.Body className={cx({ [styles.loadingBody]: loading })}>
				<div className={cx(styles.panel, { [styles.loading]: loading })}>
					{error ? (
						<div className={styles.sendNoteSection}>
							Unable to fetch, please try again later. <br />
							If this issue persists, please try reconnecting your HubSpot account in{" "}
							<ButtonLink onClick={openIntegrationSettings}>Integration settings</ButtonLink> or{" "}
							<ContactSupportLink>contact support</ContactSupportLink>.
						</div>
					) : (
						<div className={styles.infoSection}>
							{loading && <LoadingSpinner dimmer inline />}
							{contacts.length < 1 && !loading ? noneFound : activeContact && <InnerPane activeNode={activeContact} />}
						</div>
					)}
				</div>
			</SideBarItem.Body>
			{sendNotesOpen && (
				<React.Suspense fallback={<LoadingSpinner />}>
					<Modal
						noteId={noteId}
						onClose={closeSendNotes}
						options={contacts}
						defaultOption={activeContact}
						modalMode
						sendNotesAutomationIsActive={sendNotesAutomationIsActive}
						shouldShowAutomationInfoBox={sendNotesToCrmAutomationIsValid}
					/>
				</React.Suspense>
			)}
		</SideBarItem>
	);
};

export default HubspotPanel;
